import { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronRight, Lightbulb } from "lucide-react";
import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "@/lib/utils";
import type { AnalysisStatus } from "../../../../shared/analysis-types";

const thinkingMarkdownComponents: Components = {
  h1: ({ children }) => (
    <h3 className="mt-4 mb-2 text-sm font-semibold text-foreground first:mt-0">
      {children}
    </h3>
  ),
  h2: ({ children }) => (
    <h3 className="mt-4 mb-2 text-sm font-semibold text-foreground first:mt-0">
      {children}
    </h3>
  ),
  h3: ({ children }) => (
    <h4 className="mt-3 mb-1.5 text-sm font-semibold text-foreground first:mt-0">
      {children}
    </h4>
  ),
  p: ({ children }) => (
    <p className="mb-2 text-sm leading-relaxed text-sand-600 last:mb-0">
      {children}
    </p>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold text-foreground">{children}</strong>
  ),
  em: ({ children }) => <em className="italic">{children}</em>,
  ul: ({ children }) => (
    <ul className="mb-2 list-disc space-y-1 pl-5 text-sm text-sand-600 last:mb-0">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="mb-2 list-decimal space-y-1 pl-5 text-sm text-sand-600 last:mb-0">
      {children}
    </ol>
  ),
  li: ({ children }) => (
    <li className="text-sm leading-relaxed text-sand-600">{children}</li>
  ),
  code: ({ children }) => (
    <code className="rounded bg-sand-100 px-1 py-0.5 text-[0.8125rem] text-foreground">
      {children}
    </code>
  ),
  pre: ({ children }) => (
    <pre className="mb-2 overflow-x-auto rounded bg-sand-100 p-2 text-[0.8125rem] text-foreground last:mb-0">
      {children}
    </pre>
  ),
  blockquote: ({ children }) => (
    <blockquote className="my-2 border-l-2 border-sand-300 pl-3 italic text-sand-500">
      {children}
    </blockquote>
  ),
};

interface ThinkingPanelProps {
  thinking: string | null | undefined;
  status: AnalysisStatus;
  firstSectionReady: boolean;
}

export function ThinkingPanel({
  thinking,
  status,
  firstSectionReady,
}: ThinkingPanelProps) {
  const streaming = status === "analyzing" && !firstSectionReady;
  const shouldAutoExpand = streaming;

  const [userOverride, setUserOverride] = useState<boolean | null>(null);
  const expanded = userOverride ?? shouldAutoExpand;
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!expanded || !streaming) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [thinking, expanded, streaming]);

  if (!thinking || thinking.trim().length === 0) return null;

  function toggle() {
    setUserOverride(!expanded);
  }

  const label = streaming ? "Thinking" : "Model thinking";

  return (
    <div className="rounded-xl border border-sand-200 bg-sand-50">
      <button
        type="button"
        onClick={toggle}
        className="flex w-full items-center gap-2 px-4 py-3 text-left"
        aria-expanded={expanded}
      >
        <Lightbulb
          className={cn(
            "h-4 w-4 shrink-0",
            streaming ? "text-interactive animate-pulse" : "text-sand-500",
          )}
        />
        <span className="text-xs font-bold uppercase tracking-wider text-foreground">
          {label}
          {streaming && <AnimatedEllipsis />}
        </span>
        <span className="ml-auto flex items-center text-sand-400">
          {expanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </span>
      </button>
      {expanded && (
        <div
          ref={scrollRef}
          className="max-h-60 overflow-y-auto border-t border-sand-200 px-4 py-3"
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={thinkingMarkdownComponents}
          >
            {thinking}
          </ReactMarkdown>
        </div>
      )}
    </div>
  );
}

function AnimatedEllipsis() {
  return (
    <span className="ml-0.5 inline-flex">
      <span className="animate-[pulse_1.4s_ease-in-out_infinite]">.</span>
      <span className="animate-[pulse_1.4s_ease-in-out_infinite_0.2s]">.</span>
      <span className="animate-[pulse_1.4s_ease-in-out_infinite_0.4s]">.</span>
    </span>
  );
}
